import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { getApiPingUrl, probeBwofBackend, resolveApiBaseURL } from '../utils/apiBase';
import toast from 'react-hot-toast';
import { Building2 } from 'lucide-react';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [backendOk, setBackendOk] = useState(null); // null=checking, true=ok, false=fail
  const [backendWrongApi, setBackendWrongApi] = useState(false);
  const [backendError, setBackendError] = useState('');
  const [devLoginProbe, setDevLoginProbe] = useState(null);
  const [devProbeLoading, setDevProbeLoading] = useState(false);
  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  /** After login: return to deep link (e.g. /buildings) instead of always /. Open-redirect safe. */
  const getPostLoginPath = () => {
    const from = location.state?.from;
    if (from && typeof from.pathname === 'string' && from.pathname !== '/login') {
      const path = `${from.pathname}${from.search || ''}${from.hash || ''}`;
      if (path.startsWith('/') && !path.startsWith('//')) return path;
    }
    const next = searchParams.get('next') || searchParams.get('redirect');
    if (next && next.startsWith('/') && !next.startsWith('//') && next !== '/login') return next;
    return '/';
  };

  const checkBackend = () => {
    setBackendOk(null);
    setBackendError('');
    setBackendWrongApi(false);
    probeBwofBackend()
      .then((result) => {
        setBackendOk(result.ok);
        if (!result.ok) {
          setBackendWrongApi(result.wrongApi === 'electricalapp');
          setBackendError(result.message || 'Connection failed');
        }
      })
      .catch((err) => {
        setBackendOk(false);
        setBackendError(err.message || 'Connection failed');
      });
  };

  // Already logged in (e.g. opened /login while session valid) → leave login page
  useEffect(() => {
    const { isAuthenticated: authed, token } = useAuthStore.getState();
    if (authed && token) {
      navigate(getPostLoginPath(), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional once after mount; path from first paint
  }, []);

  useEffect(() => {
    let cancelled = false;
    probeBwofBackend()
      .then((result) => {
        if (cancelled) return;
        setBackendOk(result.ok);
        if (!result.ok) {
          setBackendWrongApi(result.wrongApi === 'electricalapp');
          setBackendError(result.message || 'Connection failed');
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setBackendOk(false);
          setBackendError(e.message || 'Connection failed');
        }
      });
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    console.log('[Login] Attempting login with username:', username);
    
    try {
      const result = await login(username, password);
      console.log('[Login] Login result:', result);
      
      if (result.success) {
        const target = getPostLoginPath();
        console.log('[Login] Login successful, navigating to', target);
        toast.success('Login successful');
        navigate(target, { replace: true });
      } else {
        console.error('[Login] Login failed:', result.error);
        if (import.meta.env.DEV && result.details) {
          setDevLoginProbe({
            url: `${resolveApiBaseURL().replace(/\/$/, '')}/auth/login`,
            status: result.status ?? 401,
            body: result.details,
            source: 'store',
          });
        }
        toast.error(result.error || 'Login failed');
      }
    } catch (error) {
      console.error('[Login] Login error:', error);
      console.error('[Login] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      toast.error(error.response?.data?.error || error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const runDevLoginProbe = async () => {
    if (!import.meta.env.DEV) return;
    setDevProbeLoading(true);
    setDevLoginProbe(null);
    const base = resolveApiBaseURL().replace(/\/$/, '');
    const url = `${base}/auth/login`;
    try {
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const text = await r.text();
      let body;
      try {
        body = JSON.parse(text);
      } catch {
        body = { _raw: text };
      }
      setDevLoginProbe({ url, status: r.status, body });
    } catch (err) {
      setDevLoginProbe({ url, error: err?.message || String(err) });
    } finally {
      setDevProbeLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      {backendOk === false && (
        <div className="fixed top-0 left-0 right-0 bg-red-600 text-white px-4 py-3 z-50">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 text-sm flex-wrap">
            {import.meta.env.DEV ? (
              <span className="text-center">
                Backend not running. <strong>Step 1:</strong> Open folder <code className="bg-red-700 px-1 rounded">BWOFv10</code> → double‑click <code className="bg-red-700 px-1 rounded">START_BACKEND.bat</code>. <strong>Step 2:</strong> Wait until the new window shows &quot;Server is running on port 3000&quot; and leave that window open. <strong>Step 3:</strong> Click Retry below.
              </span>
            ) : backendWrongApi ? (
              <span className="text-center">
                Wrong API on server (electricalapp on port 3000). From your PC run <code className="bg-red-700 px-1 rounded">RUN_DEPLOY.bat</code> to update nginx → BWOF API on port <strong>3010</strong>. Then Ctrl+F5 and login with <strong>james@cbsl.co.nz</strong> / <strong>password</strong>.
              </span>
            ) : (
              <span className="text-center">
                Cannot reach BWOF API. Run <code className="bg-red-700 px-1 rounded">RUN_DEPLOY.bat</code> from BWOFv10 on your PC, then Ctrl+F5. Login: <strong>james@cbsl.co.nz</strong> / <strong>password</strong>.
                {backendError ? ` (${backendError})` : ''}
              </span>
            )}
            <div className="flex gap-2 flex-shrink-0">
              {import.meta.env.DEV ? (
                <a href="http://localhost:3000/health" target="_blank" rel="noopener noreferrer" className="bg-red-700 hover:bg-red-800 px-3 py-1.5 rounded whitespace-nowrap">
                  Test: open localhost:3000/health
                </a>
              ) : (
                <a href={getApiPingUrl()} target="_blank" rel="noopener noreferrer" className="bg-red-700 hover:bg-red-800 px-3 py-1.5 rounded whitespace-nowrap">
                  Test: /api/ping
                </a>
              )}
              <button type="button" onClick={checkBackend} className="bg-red-700 hover:bg-red-800 px-3 py-1.5 rounded whitespace-nowrap">
                Retry
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        className={`flex flex-col gap-6 w-full items-stretch ${
          import.meta.env.DEV ? 'xl:flex-row xl:items-start xl:justify-center xl:max-w-5xl' : 'max-w-md'
        }`}
      >
      <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md mx-auto xl:mx-0">
        <div className="flex items-center justify-center mb-8">
          <Building2 className="w-12 h-12 text-primary-600" />
          <h1 className="ml-3 text-3xl font-bold text-gray-900">BWOF Admin</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter your username"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-gray-400">© Cornerstone Building Service Ltd</p>
        <div className="mt-3 text-center text-sm text-gray-500">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-primary-600 hover:underline font-medium"
          >
            Reload page
          </button>
        </div>
      </div>

      {import.meta.env.DEV && (
        <div className="w-full max-w-md xl:max-w-sm mx-auto xl:mx-0 rounded-lg border border-slate-600 bg-slate-900 text-slate-100 p-4 text-xs font-mono shadow-xl">
          <p className="text-slate-400 font-sans text-sm font-semibold mb-2">Dev: login API probe</p>
          <p className="text-slate-500 font-sans text-[11px] mb-3 leading-snug">
            <span className="text-amber-200/90">BAD_PASSWORD</span>: user row exists but bcrypt does not match (run{' '}
            <code className="text-slate-300">npm run fix-users</code> in backend).{' '}
            <span className="text-amber-200/90">USER_NOT_FOUND</span>: no active row for that username.
          </p>
          <button
            type="button"
            onClick={runDevLoginProbe}
            disabled={devProbeLoading}
            className="w-full mb-3 rounded bg-slate-700 px-3 py-2 text-slate-100 hover:bg-slate-600 disabled:opacity-50 font-sans text-sm"
          >
            {devProbeLoading ? 'Requesting…' : 'POST /auth/login (same fields)'}
          </button>
          {devLoginProbe && (
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all text-[11px] leading-relaxed text-emerald-200/90">
              {JSON.stringify(devLoginProbe, null, 2)}
            </pre>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

export default Login;
